import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getMarketingShopSubdomainFromHost } from '../utils/marketingShopHost';
import { applyPublicDocumentSeo, clearPublicDocumentSeo, defaultOgImageUrl } from './applyDocumentSeo';
import { buildCanonicalUrl, getPublicSiteOrigin } from './siteUrl';
import {
  alternateOgLocales,
  getSeoBundle,
  ogLocaleForLang,
  type PublicSeoLang,
} from './seoCopy';
import { usePublicSeoShopOverride } from './PublicSeoContext';

function parseShopSlugFromPath(pathname: string): string {
  const m1 = /^\/s\/([^/]+)\/?$/.exec(pathname);
  if (m1) return decodeURIComponent(m1[1]);
  const m2 = /^\/shop\/([^/]+)\/?$/.exec(pathname);
  if (m2) return decodeURIComponent(m2[1]);
  return '';
}

function resolvePageKind(
  pathname: string,
  host: string,
): 'admin' | 'home' | 'contact' | 'login' | 'shop' {
  if (pathname.startsWith('/admin')) return 'admin';
  if (pathname === '/contact') return 'contact';
  if (pathname === '/desktop-login' || pathname === '/login') return 'login';
  const sub = getMarketingShopSubdomainFromHost(host);
  if (sub || pathname.startsWith('/s/') || pathname.startsWith('/shop/')) return 'shop';
  if (pathname === '/') return 'home';
  return 'home';
}

function htmlLangFromUi(lang: string): string {
  if (lang === 'ru') return 'ru';
  if (lang === 'en') return 'en';
  return 'uz';
}

/**
 * Faqat ochiq (public) marshrutlar uchun document SEO.
 */
export function PublicSeoHead() {
  const location = useLocation();
  const { language } = useLanguage();
  const { shopOverride } = usePublicSeoShopOverride();
  const L = (language === 'ru' || language === 'en' ? language : 'uz') as PublicSeoLang;

  const payload = useMemo(() => {
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    const kind = resolvePageKind(location.pathname, host);
    const origin = getPublicSiteOrigin();
    const canonicalUrl = buildCanonicalUrl(location.pathname, '');
    const ogImage = defaultOgImageUrl();

    if (kind === 'admin') {
      return { kind: 'admin' as const };
    }

    if (kind === 'shop') {
      const slug = parseShopSlugFromPath(location.pathname);
      const sub = getMarketingShopSubdomainFromHost(host);
      const branchFromCtx = shopOverride?.branchLabel?.trim() || '';
      const branchLabel =
        branchFromCtx || (slug ? slug.replace(/[-_]+/g, ' ') : sub ? sub.replace(/[-_]+/g, ' ') : '');
      const bundle = getSeoBundle('shop', L, branchLabel);
      return {
        kind: 'shop' as const,
        bundle,
        canonicalUrl,
        ogImage,
        origin,
      };
    }

    const page = kind === 'contact' ? 'contact' : kind === 'login' ? 'login' : 'home';
    const bundle = getSeoBundle(page, L);
    return {
      kind: 'public' as const,
      page,
      bundle,
      canonicalUrl,
      ogImage,
      origin,
    };
  }, [location.pathname, L, shopOverride?.branchLabel]);

  useEffect(() => {
    if (payload.kind === 'admin') {
      clearPublicDocumentSeo();
      if (typeof document !== 'undefined') {
        document.documentElement.lang = htmlLangFromUi(language);
        document.title = 'Add-e';
        upsertAdminRobots();
      }
      return () => {
        removeAdminRobots();
      };
    }

    removeAdminRobots();

    const bundle = payload.kind === 'shop' ? payload.bundle : payload.bundle;
    const canonicalUrl = payload.canonicalUrl;
    const ogImage = payload.ogImage;
    const locale = ogLocaleForLang(L);
    const altLocales = alternateOgLocales(L);

    const jsonLd: string[] = [];
    if (payload.kind === 'public' && payload.page === 'home') {
      jsonLd.push(
        JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Add-e',
          url: `${payload.origin}/`,
          logo: ogImage,
        }),
      );
      jsonLd.push(
        JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Add-e',
          url: `${payload.origin}/`,
          publisher: { '@type': 'Organization', name: 'Add-e' },
        }),
      );
    }
    if (payload.kind === 'public' && payload.page === 'contact') {
      jsonLd.push(
        JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: bundle.title,
          url: canonicalUrl,
          isPartOf: { '@type': 'WebSite', name: 'Add-e', url: `${payload.origin}/` },
        }),
      );
    }
    if (payload.kind === 'shop') {
      jsonLd.push(
        JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Store',
          name: bundle.title,
          url: canonicalUrl,
          description: bundle.description,
        }),
      );
    }

    applyPublicDocumentSeo({
      htmlLang: htmlLangFromUi(language),
      title: bundle.title,
      description: bundle.description,
      keywords: bundle.keywords,
      canonicalUrl,
      robots: 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1',
      og: {
        type: 'website',
        siteName: 'Add-e',
        title: bundle.title,
        description: bundle.description,
        url: canonicalUrl,
        image: ogImage,
        locale,
        localeAlternate: altLocales,
      },
      twitter: {
        card: 'summary_large_image',
        title: bundle.title,
        description: bundle.description,
      },
      jsonLdScripts: jsonLd.length ? jsonLd : undefined,
    });

    return () => {
      /* boshqa marshrutga o‘tganda yangi effekt qayta yozadi */
    };
  }, [payload, language]);

  return null;
}

const ADMIN_ROBOTS_ID = 'adde-seo-admin-robots';

function upsertAdminRobots(): void {
  if (typeof document === 'undefined') return;
  let el = document.getElementById(ADMIN_ROBOTS_ID) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.id = ADMIN_ROBOTS_ID;
    el.setAttribute('name', 'robots');
    document.head.appendChild(el);
  }
  el.setAttribute('content', 'noindex,nofollow');
}

function removeAdminRobots(): void {
  if (typeof document === 'undefined') return;
  document.getElementById(ADMIN_ROBOTS_ID)?.remove();
}
